import { DELIMITER_OPTS, INTEGRATION, PatientMappingKey } from 'constants/importConfigurations';
import type {
  AdminImportConfigurationIndexFields,
  AdminImportConfigurationIndexInclude,
  AdminImportConfigurationShowFields,
  AdminImportConfigurationShowInclude,
  Typed,
} from 'services/api/fields';

import { ClassificationOptions } from './Classification';
import { Client, ClientOptions } from './Client';
import { GroupOptions } from './Group';
import { GroupTypeOptions } from './GroupType';
import { LocationOptions } from './Location';

// ---------------------------------------------------------------------------
// Mapping sub-types
// ---------------------------------------------------------------------------

export class ClassificationType {
  apiName: string;
  displayName: string;
  id: string | null;
  name: string;

  constructor(options: Partial<ClassificationType> = {}) {
    this.apiName = options.apiName || '';
    this.displayName = options.displayName || '';
    this.id = options.id || null;
    this.name = options.name || '';
  }
}

export class GroupTypeType {
  apiName: string;
  displayName: string;
  id: string | null;
  name: string;

  constructor(options: Partial<GroupTypeType> = {}) {
    this.apiName = options.apiName || '';
    this.displayName = options.displayName || '';
    this.id = options.id || null;
    this.name = options.name || '';
  }
}

export interface Mapping<MappingType, DefaultValueType> {
  columnName: string;
  type: MappingType | null;
  defaultValue: DefaultValueType | null;
}

// ---------------------------------------------------------------------------
// Config shape
// ---------------------------------------------------------------------------

interface PropertyConfig {
  importKey: string;
  olioDefault: string;
}

export interface ImportConfigurationConfig {
  dateFormat?: string;
  delimiter?: string | { label: string; value: string };
  platform?: string | { label: string; value: string } | null;
  properties: Record<string, string | PropertyConfig>;
  lookups: {
    episodes: Record<string, string | string[]>;
    plan_types: Record<string, string | string[]>;
    groups: Record<string, string | string[]>;
    group_types: Record<string, string | string[]>;
    locations: Record<string, string | string[]>;
  };
  mappings: {
    classifications: Mapping<ClassificationType, ClassificationOptions>[];
    groups: Mapping<GroupTypeOptions, GroupOptions>[];
    groupTypes: Mapping<GroupTypeType, null>[];
    locations: Mapping<null, LocationOptions>[];
    transferredFrom: Mapping<GroupTypeOptions, GroupOptions>;
  } & { [K in PatientMappingKey]?: string };
}

// ---------------------------------------------------------------------------
// Options / defaults
// ---------------------------------------------------------------------------

export interface ImportConfigurationOptions {
  client?: ClientOptions;
  config?: Partial<ImportConfigurationConfig>;
  [key: string]: any;
}

const defaultMappings = {
  classifications: [],
  groups: [],
  groupTypes: [],
  locations: [{ columnName: '', type: null, defaultValue: null }] as Mapping<null, LocationOptions>[],
  transferredFrom: { columnName: '', type: null, defaultValue: null },
};

const defaultConfig: ImportConfigurationConfig = {
  dateFormat: '%m/%d/%Y',
  delimiter: DELIMITER_OPTS[0].value,
  properties: {},
  lookups: { episodes: {}, plan_types: {}, groups: {}, group_types: {}, locations: {} },
  mappings: defaultMappings,
};

const defaults: ImportConfigurationOptions = {
  id: '',
  clientId: '',
  config: defaultConfig,
  type: '',
  updatedAt: '',
};

// ---------------------------------------------------------------------------
// Model
// ---------------------------------------------------------------------------

// Forward declare so _ImportConfiguration's constructor can return the infused type
let ImportConfiguration: new <T extends ImportConfigurationOptions>(options: T) => Typed<_ImportConfiguration<T>, T>;

class _ImportConfiguration<T extends ImportConfigurationOptions = ImportConfigurationOptions> {
  id!: string;
  clientId!: string;
  type!: string;
  client!: Client<NonNullable<T['client']>>;
  config!: ImportConfigurationConfig;

  constructor(options: T) {
    const opts = { ...defaults, ...options };
    Object.assign(this, opts);

    this.client = new Client<NonNullable<T['client']>>(opts.client!);
    this.config = {
      ...defaultConfig,
      ...opts.config,
      lookups: { ...defaultConfig.lookups, ...opts.config?.lookups },
      mappings: {
        ...defaultMappings,
        ...opts.config?.mappings,
        groupTypes: opts.config?.mappings?.groupTypes || [],
        locations: opts.config?.mappings?.locations?.length
          ? opts.config.mappings.locations
          : [{ columnName: '', type: null, defaultValue: null }],
      },
    };
  }

  get clientName() {
    return this.client.name;
  }

  get clientType() {
    return this.client.clientType;
  }

  private serializeMapping<M, D>(mapping: Mapping<M, D>) {
    const defaultValue = mapping.defaultValue as { id?: string } | null;
    const type = mapping.type as { apiName?: string; id?: string | null } | null;
    const serialized: {
      columnName: string;
      defaultValue: { id?: string } | null;
      type?: { apiName?: string; id?: string | null };
    } = {
      columnName: mapping.columnName,
      defaultValue: defaultValue ? { id: defaultValue.id } : null,
    };
    if (type) {
      serialized.type = { apiName: type.apiName, id: type.id };
    }
    return serialized;
  }

  private serializeMappings() {
    return {
      ...this.config.mappings,
      classifications: this.config.mappings.classifications.map((m) => this.serializeMapping(m)),
      groups: this.config.mappings.groups.map((m) => this.serializeMapping(m)),
      groupTypes: this.config.mappings.groupTypes.map((m) => this.serializeMapping(m)),
      locations: this.config.mappings.locations.map((m) => this.serializeMapping(m)),
    };
  }

  serialize() {
    const clientId = this.clientId || this.client.id;

    if (this.type === INTEGRATION) {
      const platform = this.config.platform;
      const { classifications, groups, groupTypes } = this.serializeMappings();
      return {
        id: this.id,
        clientId,
        type: INTEGRATION,
        config: {
          platform: typeof platform === 'string' ? platform : platform?.value,
          lookups: this.config.lookups,
          mappings: { classifications, groups, groupTypes },
        },
      };
    }

    const delimiter = this.config.delimiter;
    return {
      id: this.id,
      clientId,
      type: this.type,
      config: {
        ...this.config,
        delimiter: typeof delimiter === 'object' ? (delimiter?.value ?? ',') : delimiter,
        mappings: this.serializeMappings(),
      },
    };
  }
}

ImportConfiguration = _ImportConfiguration as typeof ImportConfiguration; // eslint-disable-line prefer-const

export default ImportConfiguration;
export { ImportConfiguration };
export type ImportConfiguration<T extends ImportConfigurationOptions = ImportConfigurationOptions> =
  _ImportConfiguration<T> & Omit<T, keyof _ImportConfiguration<T>>;

// ---------------------------------------------------------------------------
// Typed variants
// ---------------------------------------------------------------------------

export type TypedImportConfiguration<I extends AdminImportConfigurationShowInclude = never> = ImportConfiguration<
  AdminImportConfigurationShowFields<I>
>;

export type TypedImportConfigurationIndex<I extends AdminImportConfigurationIndexInclude = never> = ImportConfiguration<
  AdminImportConfigurationIndexFields<I>
>;
